// src/CartPage.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./contexts/CartContext";
import { useTheme } from "./contexts/ThemeContext";
import logo from "./assets/logo.png";
import shippingConfig from "./data/shippingConfig.json";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, changeQty, removeItem, total } = useCart();
  const { darkMode, setDarkMode } = useTheme();

  const SHIPPING_PER_GRAMS = shippingConfig.shippingPerGrams ?? 850;
  const SHIPPING_COST = shippingConfig.shippingCostPerUnit ?? 100;

  const totalWeight = cart.reduce((sum, item) => sum + (item.weight || 0) * (item.qty || 0), 0);
  const shippingUnits = Math.ceil(totalWeight / SHIPPING_PER_GRAMS);
  const shippingCharge = shippingUnits * SHIPPING_COST;
  const grandTotal = total + shippingCharge;

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-pink-100 to-white dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans pb-36">

        {/* Header */}
        {/* Header */}
        <header className="w-full py-4 shadow-sm bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-4">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={logo} alt="Bubble Butter Logo" className="h-14 w-auto object-cover rounded-lg shadow-sm" />
              <h1 className="text-2xl font-bold text-pink-600 dark:text-pink-300">Bubble Butter</h1>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate("/")} className="hover:text-pink-600 dark:hover:text-pink-300 font-semibold">
                Home
              </button>
              <button onClick={() => navigate("/products")} className="hover:text-pink-600 dark:hover:text-pink-300 font-semibold">
                Products
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden flex items-center gap-4">
              {/* Home Icon */}
              <button
                onClick={() => navigate("/")}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-6 h-6 text-pink-600 dark:text-pink-300"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-10 0h10"
                  />
                </svg>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>
          </div>
        </header>


        {/* Page Title */}
        <h1 className="text-2xl font-extrabold text-center my-6 text-pink-600 dark:text-pink-300 drop-shadow-sm">
          🛍️ Your Cart
        </h1>

        {/* Empty Cart */}
        {cart.length === 0 ? (
          <div className="text-center mt-14 space-y-6">
            <p className="text-xl opacity-80">Your cart is empty.</p>
            <button
              onClick={() => navigate("/products")}
              className="px-7 py-3 rounded-xl bg-pink-600 hover:bg-pink-700 text-white text-lg shadow-lg transition"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="grid gap-5 max-w-5xl mx-auto px-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 p-5 rounded-2xl shadow-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl border border-white/30 dark:border-gray-600/30 animate-fadeIn"
              >
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-xl shadow-md"
                />

                <div className="flex-1">
                  <div className="font-bold text-lg">{item.name}</div>
                  <div className="text-gray-600 dark:text-gray-300 text-sm mb-2">₹{item.price} each</div>

                  <div className="mt-2 flex items-center gap-3">
                    <button
                      onClick={() => changeQty(item.id, -1)}
                      className="px-3 py-1 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition active:scale-90"
                    >
                      -
                    </button>
                    <div className="text-lg font-semibold">{item.qty}</div>
                    <button
                      onClick={() => changeQty(item.id, 1)}
                      className="px-3 py-1 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition active:scale-90"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <div className="font-bold text-lg">₹{item.price * item.qty}</div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-500 hover:text-red-400 text-sm mt-3 transition active:scale-95"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

   
        {/* Sticky Checkout Bottom Bar */}
        {cart.length > 0 && (
          <div className="
            fixed bottom-0 left-0 w-full bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl 
            border-t border-white/30 dark:border-gray-700/30 shadow-lg 
            py-3 px-4 flex items-center z-50 
            gap-2 
            justify-between
            md:justify-center md:px-6 md:py-4 md:flex-row
          ">
            

            {/* ⬅️ Back Button (Mobile only) */}
            <button
              onClick={() => navigate(-1)}
              className="md:hidden h-11 w-11 flex items-center justify-center rounded-full 
                bg-gray-100 dark:bg-gray-700 shadow-md active:scale-95 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>


            {/* Subtotal + Shipping */}
            <div className="text-xs sm:text-sm opacity-90 text-left">
              <div>Subtotal: ₹{total}</div>
              <div>Shipping: ₹{shippingCharge}</div>
            </div>

            {/* Total near button on desktop */}
            <div className="text-lg font-semibold md:ml-6">
              Total:{" "}
              <span className="text-pink-600 dark:text-pink-300 font-bold text-2xl">
                ₹{grandTotal}
              </span>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="px-4 py-2 sm:px-6 sm:py-3 rounded-xl bg-pink-600 hover:bg-pink-700 text-white text-base sm:text-lg shadow-lg transition active:scale-95"
            >
              Checkout
            </button>

          </div>
        )}


      </div>
    </div>
  );
}
